/**
 * The pieces of the AI Team page (ADR 0259): an employee's avatar, a finding
 * card in the feed's voice or the by-job view's titled form, a job in the
 * list, the chosen job's panel, and a dismissed finding with its Reopen.
 */

import type { ReactElement } from "react";

import { AI_EMPLOYEE_ORDER, AI_EMPLOYEES } from "@roof-hawk/core";
import type {
  AiEmployee,
  AiFinding,
  AiJobGroup,
  AiReason,
  aiTeamCounts,
  Job,
} from "@roof-hawk/core";

import { navigate } from "../../lib/hooks";
import { jobLink } from "../../lib/jobLink";

export const REASON_LABEL: Record<AiReason, string> = {
  wrong: "Wrong",
  fine: "Right, but fine",
};

export const EVIDENCE_LABEL: Record<string, string> = {
  Emails: "See the email",
  Texts: "See the texts",
  Notes: "See the note",
};

export const BID_LABEL: Record<string, string> = {
  retail: "Retail",
  repair: "Repair",
  insurance: "Insurance",
};

/** What every finding card needs to say about its job. */
export interface JobFacts {
  number: string;
  name: string;
  stage: string;
  rep: string;
  bid: string;
  value: string;
}

/** The customer's name, or the job number when there is no contact. */
export function nameOfJob(
  job: Job | undefined,
  contactName: ReadonlyMap<string, string | null>,
): string {
  if (!job) return "No job";
  return (
    (job.contact_id && contactName.get(job.contact_id)) ||
    `Job #${job.job_number}`
  );
}

/** "today", "1 day", "9 days". */
export function waited(days: number): string {
  return days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`;
}

export function partOfDay(now: Date): string {
  const hour = now.getHours();
  return hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
}

export function sum(
  counts: ReturnType<typeof aiTeamCounts>,
  key: "flagged" | "wrong",
): number {
  return AI_EMPLOYEE_ORDER.reduce((n, e) => n + counts[e][key], 0);
}

export function Avatar({
  employee,
  size = "md",
}: {
  employee: AiEmployee;
  size?: "xs" | "sm" | "md" | "lg";
}) {
  return (
    <span
      className={`ait-av ait-av-${size} ait-av-${employee}`}
      aria-hidden="true"
    >
      {AI_EMPLOYEES[employee].name[0]}
    </span>
  );
}

export function Pct({ finding }: { finding: AiFinding }) {
  return (
    <span className={`ait-pct${finding.likely ? "" : " sure"}`}>
      {finding.likely ? "Looks like" : "Sure"} ·{" "}
      {Math.round(finding.probability * 100)}%
    </span>
  );
}

export function FindingCard({
  finding,
  job,
  days,
  voice,
  busy,
  onDismiss,
}: {
  finding: AiFinding;
  job: JobFacts | null;
  days: number;
  /** The feed's way: a message from the employee. Otherwise a titled card. */
  voice: boolean;
  busy: boolean;
  onDismiss: (reason: AiReason) => void;
}) {
  const employee = AI_EMPLOYEES[finding.employee];
  const jobId = finding.jobId;
  const evidence = finding.card;
  const label = `${employee.name}: ${finding.title}`;
  return (
    <article className="ait-card ait-finding" aria-label={label}>
      {voice ? (
        <div className="ait-msg">
          <Avatar employee={finding.employee} size="lg" />
          <div className="ait-msg-t">
            <div>
              <b>{employee.name}</b>{" "}
              <span className="ait-muted">
                {employee.role} · {days === 0 ? "today" : `${waited(days)} ago`}
              </span>
            </div>
            <div className="ait-voice">{finding.voice}</div>
            {job ? (
              <div className="ait-muted">
                <span className="ait-job">
                  {job.number} {job.name}
                </span>{" "}
                · {job.stage}
              </div>
            ) : null}
          </div>
          <Pct finding={finding} />
        </div>
      ) : (
        <div className="ait-cardhead">
          <div>
            <div className="ait-title">
              {job ? (
                <span className="ait-job">
                  {job.number} {job.name}:{" "}
                </span>
              ) : null}
              {finding.title}
            </div>
            <div className="ait-muted">
              Waiting {waited(days)}
              {job ? ` · ${job.rep}’s job` : ""}
            </div>
          </div>
          <Pct finding={finding} />
        </div>
      )}

      {finding.summary ? <p className="ait-what">{finding.summary}</p> : null}

      <div className="ait-pair">
        <div className="ait-said">
          {finding.said ? (
            <>
              <div className="ait-lbl">{finding.said.label}</div>“
              {finding.said.text}”
            </>
          ) : (
            <span className="ait-muted">No message quoted.</span>
          )}
        </div>
        <div className="ait-ne" aria-hidden="true">
          ≠
        </div>
        {finding.record ? (
          <div className="ait-rec">
            <div className="ait-lbl">{finding.record.label}</div>
            {finding.record.text}
          </div>
        ) : (
          <div />
        )}
      </div>

      <div className="ait-acts">
        {jobId ? (
          <>
            <button
              type="button"
              className="ait-btn"
              onClick={() => navigate(jobLink(jobId))}
            >
              Open job {job?.number}
            </button>
            {evidence ? (
              <button
                type="button"
                className="ait-link"
                onClick={() => navigate(jobLink(jobId, evidence))}
              >
                {EVIDENCE_LABEL[evidence]} →
              </button>
            ) : null}
          </>
        ) : null}
        <button
          type="button"
          className="ait-btn ait-creed"
          disabled
          title="Coming soon: sends this to Creed in Slack to dig into"
        >
          Ask Creed to dig in <span className="ait-soon">Soon</span>
        </button>
        <span className="ait-sp" />
        <button
          type="button"
          className="ait-btn ait-wrong"
          disabled={busy}
          onClick={() => onDismiss("wrong")}
        >
          ✗ Wrong
        </button>
        <button
          type="button"
          className="ait-btn ait-fine"
          disabled={busy}
          onClick={() => onDismiss("fine")}
        >
          ✓ Right, but fine
        </button>
      </div>
    </article>
  );
}

export function JobRow({
  group,
  job,
  on,
  days,
  onPick,
}: {
  group: AiJobGroup;
  job: JobFacts | null;
  on: boolean;
  /** "9 days", or nothing for a job with only dismissed findings. */
  days: string | null;
  onPick: () => void;
}) {
  const employees = [
    ...new Set(
      (group.open.length ? group.open : group.dismissed).map((f) => f.employee),
    ),
  ];
  return (
    <button
      type="button"
      className={`ait-jobrow${on ? " on" : ""}`}
      aria-pressed={on}
      onClick={onPick}
    >
      <span className="ait-jobrow-t">
        <b>
          {job?.number} {job?.name}
        </b>
        <span className="ait-muted">
          {job?.stage} · {job?.rep}
        </span>
      </span>
      <span className="ait-avs">
        {employees.map((e) => (
          <Avatar key={e} employee={e} size="xs" />
        ))}
      </span>
      {days === null ? null : (
        <span className="ait-muted ait-days">{days}</span>
      )}
    </button>
  );
}

export function JobPanel({
  group,
  job,
  card,
  history,
}: {
  group: AiJobGroup;
  job: JobFacts | null;
  card: (f: AiFinding) => ReactElement;
  history: (f: AiFinding) => ReactElement;
}) {
  const by = [...new Set(group.open.map((f) => f.employee))].length;
  return (
    <div>
      <div className="ait-card ait-jobhead">
        <div>
          <h3>
            {job?.number} {job?.name}
          </h3>
          <div className="ait-muted">
            {[job?.bid, job?.rep].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span className="ait-sp" />
        <span className="ait-stage">{job?.stage}</span>
        <b className="ait-value">{job?.value}</b>
        <button
          type="button"
          className="ait-btn ait-primary"
          onClick={() => navigate(jobLink(group.jobId))}
        >
          Open job
        </button>
      </div>
      {group.open.length ? (
        <div className="ait-say">
          {by === 1 ? "One AI employee" : `${by} AI employees`} flagged
          something here
        </div>
      ) : null}
      {group.open.map((f) => (
        <div key={f.key}>
          <div className="ait-by">
            <Avatar employee={f.employee} size="sm" />{" "}
            <b>{AI_EMPLOYEES[f.employee].name}</b>{" "}
            <span className="ait-muted">{AI_EMPLOYEES[f.employee].role}</span>
          </div>
          {card(f)}
        </div>
      ))}
      <div className="ait-card">
        <h4>History on this job</h4>
        {group.dismissed.length ? (
          group.dismissed.map(history)
        ) : (
          <div className="ait-muted">Nothing dismissed on this job.</div>
        )}
      </div>
    </div>
  );
}

export function DismissedRow({
  finding,
  job,
  by,
  busy,
  onReopen,
}: {
  finding: AiFinding;
  job: JobFacts | null;
  by: string;
  busy: boolean;
  onReopen: () => void;
}) {
  const when = finding.dismissedAt
    ? new Date(finding.dismissedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <div className="ait-drow">
      <Avatar employee={finding.employee} size="sm" />
      <div className="ait-drow-t">
        <div>
          {job ? (
            <b className="ait-job">
              {job.number} {job.name}
            </b>
          ) : null}
          {job ? ": " : ""}
          {finding.title}{" "}
          {finding.summary ? (
            <span className="ait-muted">{finding.summary}</span>
          ) : finding.said ? (
            <span className="ait-muted">“{finding.said.text}”</span>
          ) : null}
        </div>
        <div className="ait-muted">
          <span className={`ait-how ${finding.reason ?? "none"}`}>
            {finding.reason ? REASON_LABEL[finding.reason] : "Dismissed"}
          </span>{" "}
          {by}, {when}
        </div>
      </div>
      {finding.reopenable ? (
        <button
          type="button"
          className="ait-link"
          disabled={busy}
          onClick={onReopen}
        >
          Reopen
        </button>
      ) : null}
    </div>
  );
}
